import React from "react";
import { ResponsiveLine } from "@nivo/line";
import { twMerge } from "tailwind-merge";
import { BaseTag } from "./Tag";

// Exponential decay in log space for a smooth, reference-matching curve
function expDecayCurve(x: number, A = 10000, k = 1.5) {
  return A * Math.exp(-k * Math.log10(x / 0.0001));
}

// Densify the data for a smooth curve
const minScore = 0.0001;
const maxScore = 100;
const densePoints = 120;
const denseData = Array.from({ length: densePoints }, (_, i) => {
  // Logarithmic spacing for scores
  const logMin = Math.log10(minScore);
  const logMax = Math.log10(maxScore);
  const logScore = logMin + (i / (densePoints - 1)) * (logMax - logMin);
  const score = Math.pow(10, logScore);
  return { x: score, y: expDecayCurve(score) };
});

export interface EpssChartProps {
  score: number; // The EPSS score to mark
  className?: string;
  /**
   * Optional height for the chart container (e.g. 320, '320px', '20rem').
   * If not provided, defaults to h-80 md:h-96.
   */
  height?: number | string;
}

export const EpssChart: React.FC<EpssChartProps> = ({ score, className, height }) => {
  // Clamp score to valid log scale domain
  const safeScore = Math.max(score, 0.0001);
  // Interpolate y for the marker using the smooth curve
  function getY(x: number) {
    return expDecayCurve(x);
  }
  const interpolatedY = getY(safeScore);
  const chartData = denseData;

  const data = [
    {
      id: "EPSS PDF",
      color: "hsl(256, 80%, 70%)",
      data: chartData,
    },
  ];

  const TagLayer = ({ xScale, yScale, height, width }: { xScale: (v: number) => number; yScale: (v: number) => number; height: number; width: number }) => {
    const x = xScale(safeScore);
    let y = yScale(interpolatedY);
    // Match marker position
    const minHeight = height * 0.1;
    y = Math.min(y, yScale(0) - minHeight);
    const tagWidth = 280; // matches foreignObject width
    const margin = 18; // Tighter spacing between marker and tag
    // If the tag would overflow the right edge, flip to the left
    const flipLeft = x + margin + tagWidth > width;
    const tagX = flipLeft ? x - (tagWidth / 1.7) - (margin * 2) : x + margin;
    return (
      <foreignObject x={tagX} y={y - 18} width={tagWidth} height={40} style={{ pointerEvents: 'none' }}>
        <div className="flex items-center">
          <BaseTag
            size="xs"
            className={twMerge(
              safeScore >= 10 ? "bg-chariot-status-critical border-chariot-stroke-critical text-white" :
              safeScore >= 1 ? "bg-chariot-status-high border-chariot-stroke-high text-white" :
              safeScore >= 0.1 ? "bg-chariot-status-medium border-chariot-stroke-medium text-white" :
              safeScore >= 0.01 ? "bg-chariot-status-low border-chariot-stroke-low text-white" :
              "bg-chariot-status-info border-chariot-stroke-info text-white",
              "text-xs font-bold px-4 py-1 rounded-full"
            )}
          >
            {`EPSS Score of ${safeScore.toFixed(4)}`}
          </BaseTag>
        </div>
      </foreignObject>
    );
  };

  // Custom marker and vertical dashed line for the selected score
  const MarkerLineLayer = ({ xScale, yScale, height }: { xScale: (v: number) => number; yScale: (v: number) => number; height: number }) => {
    const x = xScale(safeScore);
    let y = yScale(interpolatedY);
    const yAxisBottom = yScale(0);
    // Ensure line is at least 10% of chart height, but allow it to be taller
    const minHeight = height * 0.1;
    y = Math.min(y, yAxisBottom - minHeight);
    return (
      <g>
        {/* Dashed vertical line */}
        <line
          x1={x}
          y1={y}
          x2={x}
          y2={yAxisBottom}
          stroke="#FFD600"
          strokeWidth={3}
          strokeDasharray="6 6"
        />
        {/* Marker circle (flag top) */}
        <circle
          cx={x}
          cy={y}
          r={10}
          fill="#18122B"
          stroke="#FFD600"
          strokeWidth={2}
        />
        <circle
          cx={x}
          cy={y}
          r={7}
          fill="#FFD600"
        />
      </g>
    );
  };

  // Determine chart container style
  const chartContainerStyle = height ? { height: typeof height === 'number' ? String(height) + 'px' : String(height) } : undefined;
  const chartContainerClass = height ? "w-full min-w-[200px]" : "w-full min-w-[200px] h-80 md:h-96";

  // Determine effective height as a number (px)
  let effectiveHeight = 384; // Tailwind h-96 = 384px
  if (height) {
    if (typeof height === 'number') effectiveHeight = height;
    else if (typeof height === 'string' && height.endsWith('px')) effectiveHeight = parseInt(height);
    else if (typeof height === 'string' && /^\d+$/.test(height)) effectiveHeight = parseInt(height);
    // else: could be rem/em/%/auto, fallback to default
  }

  // Adjust y-axis ticks and font size for small heights
  const isShort = effectiveHeight <= 160;
  const yTickValues = isShort ? [0, 2500, 5000, 7500, 10000] : undefined;
  const axisFontSize = isShort ? 10 : 14;

  return (
    <div className={twMerge("w-full bg-chariot-background-primary rounded-lg p-2", className)}>
      <div className={chartContainerClass} style={chartContainerStyle}>
        <ResponsiveLine
          data={data}
          xScale={{ type: "log", base: 10, min: 0.0001, max: 100 }}
          yScale={{ type: "linear", min: 0, max: 10000 }}
          curve="monotoneX"
          axisBottom={{
            legend: "EPSS Score",
            legendOffset: 50,
            legendPosition: "middle",
            tickValues: [0.0001, 0.01, 0.1, 1, 10, 100],
            format: (v: unknown) => typeof v === 'number' ? v.toString() : String(v),
            tickSize: 8,
            tickPadding: 8,
            tickRotation: 0,
          }}
          axisLeft={{
            legend: "Number of Vulnerabilities",
            legendOffset: -80,
            legendPosition: "middle",
            tickSize: 8,
            tickPadding: 8,
            format: (v: unknown) => typeof v === 'number' ? v.toString() : String(v),
            tickValues: yTickValues,
          }}
          margin={{ top: 20, right: 30, bottom: 60, left: 100 }}
          enableArea={true}
          areaOpacity={0.2}
          colors={["#98242D"]}
          lineWidth={0}
          enablePoints={false}
          pointSize={0}
          pointColor="#FFD600"
          pointBorderWidth={2}
          pointBorderColor="#FFD600"
          useMesh={true}
          layers={["grid", "markers", "areas", "lines", "axes", "legends", MarkerLineLayer, (props) => TagLayer({ ...props, width: props.width, height: props.height })]}
          gridXValues={[]}
          gridYValues={[]}
          theme={{
            axis: {
              ticks: {
                text: {
                  fill: "rgb(var(--chariot-text-secondary))",
                  fontSize: axisFontSize,
                },
              },
              legend: {
                text: {
                  fill: "rgb(var(--chariot-text-secondary))",
                  fontWeight: 600,
                },
              },
              domain: {
                line: {
                  stroke: "rgb(var(--chariot-stroke))",
                  strokeWidth: 2,
                },
              },
            },
            grid: {
              line: {
                stroke: "none",
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default EpssChart; 